import { Box, Button, Typography } from '@mui/material'
import { editUser } from 'api/management'
import { useRootLoaderData } from 'components/Root.loader'
import { AUTHORITIES } from 'config'
import { useAlert, useConfirm } from 'hooks/useDialog'
import useLeavingPrompt from 'hooks/useLeavingPrompt'
import useLogout from 'hooks/useLogout'
import { useMemo, useCallback } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useNavigate, Link, useParams } from 'react-router-dom'

import DeleteUserButton from './DeleteUserButton'
import NotFoundMessage from './NotFoundMessage'
import UserFormFields, { FormData } from './UserFormFields'
import { useLoaderData } from './UsersConfig.loader'

export default function UsersEdit() {
  const alert = useAlert()
  const confirm = useConfirm()
  const { logout } = useLogout()
  const navigate = useNavigate()
  const userId = parseInt(useParams().userId!)
  const users = useLoaderData().users
  const currentUser = useMemo(() => users.find((x) => x.userID === userId), [userId, users])
  const { profile: userProfile } = useRootLoaderData()

  const methods = useForm<FormData>({
    defaultValues: currentUser,
  })
  const { isDirty, isSubmitted, isSubmitting } = methods.formState

  useLeavingPrompt({
    message: ['設定編集をキャンセルします', '入力したデータは保存されません。'],
    when: isDirty && !(isSubmitted || isSubmitting),
  })

  const onSubmit = useCallback(
    async (data: FormData) => {
      const leavingAdmin =
        userProfile.id === data.userID &&
        userProfile.authority === AUTHORITIES.ADMIN &&
        data.authority !== AUTHORITIES.ADMIN
      if (leavingAdmin) {
        const { status } = await confirm({
          message: [
            'ログインユーザーの権限をシステム管理者から変更しますか？',
            '変更後はログアウトされます',
          ],
        })
        if (status === 'canceled') {
          return
        }
      }
      try {
        await editUser(data)
      } catch {
        return alert({ message: 'ユーザー設定の保存が失敗しました' })
      }
      if (leavingAdmin) {
        await alert({ message: 'ユーザー設定が保存されました' })
        return await logout()
      }
      alert({ message: 'ユーザー設定が保存されました' })
      navigate('..')
    },
    [alert, confirm, logout, navigate, userProfile],
  )

  if (!currentUser) {
    return <NotFoundMessage />
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <Box display="flex" justifyContent="space-between">
          <Typography component="h3" variant="h6">
            ユーザー設定の編集
          </Typography>
          <Box display="flex">
            <Button
              component={Link}
              variant="outlined"
              color="secondary"
              to=".."
              data-testid="cancel-button"
            >
              キャンセル
            </Button>
            <DeleteUserButton user={currentUser} />
            <Button type="submit" variant="contained" color="primary" data-testid="save-button">
              保存
            </Button>
          </Box>
        </Box>
        <UserFormFields editMode />
      </form>
    </FormProvider>
  )
}
